import { apiClient } from './api';
import type { DataProvider } from '@refinedev/core';
import { API_ENDPOINTS } from '@/constants';
import { STRING_MAX } from '@/constants/validation';

// API response types
// Note: List endpoints return { data: [...], total, skip, take }
// Single-entity endpoints (getOne, create, update, delete) return the entity directly
type ApiListResponse<T> = {
  data: Array<T>;
  total: number;
};

const getMutationConfig = (meta: unknown): { headers: Record<string, string> } | undefined => {
  if (meta === null || typeof meta !== 'object' || Array.isArray(meta)) return undefined;

  const headers = (meta as Record<string, unknown>).headers;
  if (headers === null || typeof headers !== 'object' || Array.isArray(headers)) return undefined;

  return { headers: headers as Record<string, string> };
};

// Resource mapping
const resourceMap: Record<string, string> = {
  customers: API_ENDPOINTS.CUSTOMERS,
  companies: API_ENDPOINTS.COMPANIES,
  extras: API_ENDPOINTS.EXTRAS,
  fees: API_ENDPOINTS.FEES,
  insurances: API_ENDPOINTS.INSURANCES,
  seasons: API_ENDPOINTS.SEASONS,
  'vehicle-groups': API_ENDPOINTS.GROUPS,
  'vehicle-models': API_ENDPOINTS.MODELS,
  vehicles: API_ENDPOINTS.VEHICLES,
  stations: API_ENDPOINTS.STATIONS,
  reservations: API_ENDPOINTS.RESERVATIONS,
  rentals: API_ENDPOINTS.ADMIN_RENTALS,
  'global-vehicle-models': API_ENDPOINTS.ADMIN_GLOBAL_VEHICLE_MODELS,
  'security-events': API_ENDPOINTS.ADMIN_SECURITY_EVENTS,
  users: API_ENDPOINTS.USERS,
  'admin-users': API_ENDPOINTS.ADMIN_USERS,
  'block-types': API_ENDPOINTS.BLOCK_TYPES,
  'vehicle-blocks': API_ENDPOINTS.VEHICLE_BLOCKS,
  'schedule-exceptions': API_ENDPOINTS.SCHEDULE_EXCEPTIONS,
};

export const dataProvider: DataProvider = {
  getList: async ({ resource, pagination, filters, sorters }) => {
    // Safe: resource is constrained to registered Refine resources
    // eslint-disable-next-line security/detect-object-injection
    const endpoint = resourceMap[resource];
    if (!endpoint) {
      throw new Error(`[dataProvider] Unknown resource: "${resource}" - not in resourceMap`);
    }

    // Convert Refine page/pageSize to backend skip/take
    const page = pagination?.currentPage ?? 1;
    const pageSize = pagination?.pageSize ?? 10;
    const skip = (page - 1) * pageSize;
    const take = pageSize;

    // Map Refine CrudFilters to query params
    const filterParams: Record<string, string> = {};
    if (filters) {
      for (const filter of filters) {
        if ('field' in filter && filter.value != null && filter.value !== '') {
          filterParams[filter.field] =
            filter.field === 'search'
              ? String(filter.value).trim().slice(0, STRING_MAX.IDENTIFIER)
              : String(filter.value);
        }
      }
    }

    // Map Refine sorters to backend sort params (single-column sort)
    const sorter = Array.isArray(sorters) && sorters.length > 0 ? sorters[0] : undefined;
    const sortField =
      sorter && 'field' in sorter && typeof sorter.field === 'string' ? sorter.field : undefined;
    const rawOrder =
      sorter && 'order' in sorter && typeof sorter.order === 'string' ? sorter.order : undefined;
    const rawOrderValue = rawOrder ? rawOrder.toLowerCase() : undefined;
    const sortOrder =
      rawOrderValue === 'asc' || rawOrderValue === 'ascend'
        ? 'asc'
        : rawOrderValue === 'desc' || rawOrderValue === 'descend'
          ? 'desc'
          : undefined;

    const response = await apiClient.get<ApiListResponse<Record<string, unknown>>>(endpoint, {
      params: {
        skip,
        take,
        ...filterParams,
        ...(sortField ? { sort: sortField } : {}),
        ...(sortOrder ? { order: sortOrder } : {}),
      },
    });

    return {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
      data: response.data.data as any,
      total: response.data.total,
    };
  },

  getOne: async ({ resource, id, meta }) => {
    // Safe: resource is constrained to registered Refine resources
    // eslint-disable-next-line security/detect-object-injection
    const endpoint = resourceMap[resource];
    if (!endpoint) {
      throw new Error(`[dataProvider] Unknown resource: "${resource}" - not in resourceMap`);
    }
    const idString = String(id);
    // Note: Backend returns entity directly (no { data: entity } wrapper)
    const config = getMutationConfig(meta);
    const response = config
      ? await apiClient.get<Record<string, unknown>>(`${endpoint}/${idString}`, config)
      : await apiClient.get<Record<string, unknown>>(`${endpoint}/${idString}`);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
    return { data: response.data as any };
  },

  create: async ({ resource, variables, meta }) => {
    // Safe: resource is constrained to registered Refine resources
    // eslint-disable-next-line security/detect-object-injection
    const endpoint = resourceMap[resource];
    if (!endpoint) {
      throw new Error(`[dataProvider] Unknown resource: "${resource}" - not in resourceMap`);
    }
    // Note: Backend returns entity directly (no { data: entity } wrapper)
    const config = getMutationConfig(meta);
    const response = config
      ? await apiClient.post<Record<string, unknown>>(endpoint, variables, config)
      : await apiClient.post<Record<string, unknown>>(endpoint, variables);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
    return { data: response.data as any };
  },

  update: async ({ resource, id, variables, meta }) => {
    // Safe: resource is constrained to registered Refine resources
    // eslint-disable-next-line security/detect-object-injection
    const endpoint = resourceMap[resource];
    if (!endpoint) {
      throw new Error(`[dataProvider] Unknown resource: "${resource}" - not in resourceMap`);
    }
    const idString = String(id);
    // Note: Backend returns entity directly (no { data: entity } wrapper)
    const config = getMutationConfig(meta);
    const response = config
      ? await apiClient.patch<Record<string, unknown>>(`${endpoint}/${idString}`, variables, config)
      : await apiClient.patch<Record<string, unknown>>(`${endpoint}/${idString}`, variables);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
    return { data: response.data as any };
  },

  deleteOne: async ({ resource, id, variables, meta }) => {
    // Safe: resource is constrained to registered Refine resources
    // eslint-disable-next-line security/detect-object-injection
    const endpoint = resourceMap[resource];
    if (!endpoint) {
      throw new Error(`[dataProvider] Unknown resource: "${resource}" - not in resourceMap`);
    }
    const idString = String(id);
    // Note: Backend returns entity directly (no { data: entity } wrapper)
    const mutationConfig = getMutationConfig(meta);
    const deleteConfig =
      variables === undefined
        ? mutationConfig
        : {
            ...(mutationConfig ?? {}),
            data: variables,
          };
    const response = deleteConfig
      ? await apiClient.delete<Record<string, unknown>>(`${endpoint}/${idString}`, deleteConfig)
      : await apiClient.delete<Record<string, unknown>>(`${endpoint}/${idString}`);
    // A global vehicle model delete answers 204 with no body; Refine still wants a record.
    if (
      resource === 'global-vehicle-models' &&
      (response.data === null || typeof response.data !== 'object')
    ) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
      return { data: { id } as any };
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
    return { data: response.data as any };
  },

  getApiUrl: () => apiClient.defaults.baseURL ?? '',
};
